import React, {useCallback, useEffect, useMemo, useState} from "react";
import useAccessLevels from "../../../../hooks/use-access-levels";
import DraggablePopover from "../index";
import {Box, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Tooltip, Typography} from "@mui/material";
import {CloseOutlined, ControlCamera, EditOutlined, VisibilityRounded} from "@mui/icons-material";
import {ProductOnPurchaseListDataGridColumnNames} from "../../../../../core/constants/table/data-grid-column-keys";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnPinnedTypes, DataGridColumnTypes, useDataGridSavedState} from "../../../../../packages/data-grid";
import useIsMounted from "../../../../hooks/use-is-mounted";
import axios from "axios";
import DateUtils from "../../../../../core/services/date-utils";
import PartnerDataGrid from "../../../../containers/data-grid";
import Api from "../../../../../core/services/api/api";
import EditPartPurchaseEtaDialog from "../../../dialogs/edit-part-purchase-eta";
import Button from "../../../base/button/button";
import Dialog from "../../../base/dialog";
import useCountryProvinceCache from "../../../../hooks/use-country-province-cache";

const ProductOnPurchasePopover = ({
                                      children,
                                      wrapperProps,
                                      buttonProps,
                                      iconProps,
                                      partNo,
                                      warehouseCode,
                                      shippingCompanies,
                                      cancelSelector,
                                      onPurchaseQty,
                                  }) => {
    const operatorAccessLevels = useAccessLevels();
    const canRenderPopover = useMemo(() =>
            operatorAccessLevels.packages.lookup.productOnPurchaseList &&
            !!partNo &&
            !!warehouseCode &&
            onPurchaseQty > 0
        , [operatorAccessLevels.packages.lookup.productOnPurchaseList, partNo, warehouseCode, onPurchaseQty]);

    return (
        <>
            <DraggablePopover
                popupId={`product-on-purchase-popover-${partNo}-${warehouseCode}`}
                canRenderPopover={canRenderPopover}
                wrapperProps={wrapperProps}
                buttonProps={buttonProps}
                iconProps={iconProps}
                popoverElement={
                    <ProductOnPurchaseList
                        partNo={partNo}
                        warehouseCode={warehouseCode}
                        shippingCompanies={shippingCompanies}
                    />}
                width={1400}
                height={600}
                iconOnRight={false}
                draggableProps={{
                    cancel: `.draggable-popover-cancel ${cancelSelector ? `, ${cancelSelector}` : ""}`
                }}
                popperProps={{
                    slotProps: {
                        root: {
                            className: "product-on-purchase-popover-class",
                        },
                    },
                }}
            >
                {children}
            </DraggablePopover>
        </>
    );
}

const initialEditPartPurchaseEtaDialogState = {open: false, record: null};

const initialViewNoteDialogState = {open: false, note: "", title: ""};

const ProductOnPurchaseList = ({close, width, height, partNo, warehouseCode, shippingCompanies = []}) => {
    const {countries} = useCountryProvinceCache(undefined, true);
    const operatorAccessLevels = useAccessLevels();
    const canManagePurchaseList = operatorAccessLevels.packages.lookup.manageProductOnPurchaseList;
    const [purchases, setPurchases] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editPartPurchaseEtaDialogState, setEditPartPurchaseEtaDialogState] = useState(initialEditPartPurchaseEtaDialogState);
    const [viewNoteDialogState, setViewNoteDialogState] = useState(initialViewNoteDialogState);
    const isMounted = useIsMounted();


    const dataGridSaveConfig = useMemo(() => {
        return ({
            name: "ProductOnPurchaseListDataGrid",
            version: canManagePurchaseList ? 3 : 4,
        });
    }, [canManagePurchaseList]);

    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                title: "Purchase No",
                name: ProductOnPurchaseListDataGridColumnNames.poNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 80,
            },
            {
                title: "Vendor",
                name: ProductOnPurchaseListDataGridColumnNames.vendorCode,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 50,
            },
            {
                title: "Qty",
                name: ProductOnPurchaseListDataGridColumnNames.qty,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 20,
            },
            {
                title: "ETA",
                name: ProductOnPurchaseListDataGridColumnNames.eta,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "Shipped via",
                name: ProductOnPurchaseListDataGridColumnNames.shipVia,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "Ship To",
                name: ProductOnPurchaseListDataGridColumnNames.shipTo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 120,
            },
            {
                title: "Tracking No",
                name: ProductOnPurchaseListDataGridColumnNames.trackingNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 150,
            },
            {
                title: "Note",
                name: ProductOnPurchaseListDataGridColumnNames.note,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 200,
            },
            ...(canManagePurchaseList ? [{
                name: ProductOnPurchaseListDataGridColumnNames.action,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                pinnedToggleable: false,
                resizable: false,
                visibilityToggleable: false,
                reorderable: false,
                pinned: true,
                pinnedType: DataGridColumnPinnedTypes.right,
                width: 30,
            }] : []),
        ], [canManagePurchaseList]);

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Fetch the purchases based on the part number and warehouse code.
     * @type {(function(): Promise<void>)}
     */
    const getPurchases = useCallback(async (cancelToken) => {
        setLoading(true);
        const response = await Api.getPurchasesETAForPart({partNo, warehouseCode}, cancelToken)
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setPurchases(response.data ?? []);
        }
        setLoading(false);
    }, [partNo, warehouseCode, isMounted]);

    /**
     * Effect to fetch purchases when the component mounts or when partNo or warehouseCode changes.
     */
    useEffect(() => {
        const cancelTokenSource = axios.CancelToken.source();
        getPurchases(cancelTokenSource.token).then();
        return () => cancelTokenSource.cancel();
    }, [getPurchases, partNo, warehouseCode]);

    /**
     * Open the edit part purchase ETA dialog with the selected record.
     * @type {(function(*): void)}
     */
    const openEditPartPurchaseEtaDialogState = useCallback((record) => {
        if (!operatorAccessLevels.packages.lookup.manageProductOnPurchaseList) {
            return;
        }
        setEditPartPurchaseEtaDialogState({
            open: true,
            record: record,
        });
    }, [operatorAccessLevels.packages.lookup.manageProductOnPurchaseList]);

    /**
     * Close the update popover.
     * @type {(function(): void)}
     */
    const closeEditPartPurchaseEtaDialog = useCallback(() => {
        setEditPartPurchaseEtaDialogState(initialEditPartPurchaseEtaDialogState);
    }, []);

    /**
     * Update the purchase record in the state.
     * @type {(function(*): Promise<void>)}
     */
    const updateRecord = useCallback(async (record) => {
        setPurchases(prevPurchases => prevPurchases.map(purchase => purchase.purchaseId === record.purchaseId ? {...purchase, ...record} : purchase));
    }, [setPurchases]);


    /**
     * Open the view note dialog with the selected purchase note.
     * @type {(function(*): void)}
     */
    const openViewNoteDialog = useCallback((purchase) => {
        setViewNoteDialogState({
            open: true,
            note: purchase.note,
            title: `Note of Purchase No: ${purchase.purchaseNumber}`,
        });
    }, [])

    /**
     * close the view note dialog.
     * @type {(function(): void)|*}
     */
    const closeViewNoteDialog = useCallback(() => {
        setViewNoteDialogState(initialViewNoteDialogState);
    }, []);

    const dataGridRows = useMemo(() => {
        return purchases.map((purchase) => {
            const now = DateUtils.localNow();
            const etaDate = DateUtils.serverToLocal(purchase.eta);
            const etaValid = purchase.eta != null && etaDate.isValid();
            const etaPassed = etaDate.isBefore(now);

            let countryName = "";
            let provinceName = "";
            for (const c of countries) {
                const foundProvince = c.provinces?.find(p => p.code === purchase.shipmentProvinceCode);
                if (!!foundProvince) {
                    provinceName = foundProvince?.name ?? "";
                    countryName = c.name ?? "";
                    break;
                }
            }

            const noteLength = purchase.note?.toString()?.length ?? 0;

            return (
                /** @type DataGridRow*/
                {
                    key: purchase.purchaseId,
                    data: purchase,
                    cells: {
                        [ProductOnPurchaseListDataGridColumnNames.poNo]: purchase.purchaseNumber,
                        [ProductOnPurchaseListDataGridColumnNames.vendorCode]: purchase.vendorCode,
                        [ProductOnPurchaseListDataGridColumnNames.qty]: purchase.qty,
                        [ProductOnPurchaseListDataGridColumnNames.trackingNo]: purchase.trackingNumber,
                        [ProductOnPurchaseListDataGridColumnNames.eta]:
                            <Tooltip title={!etaValid ? "" : `Delivery ${etaPassed ? "was estimated to arrive at" : "is estimated to arrive at "} ${etaDate.format(DateUtils.dateFormat)}`}>
                                {
                                    !etaValid &&
                                    <Typography variant={'body2'}>N/A</Typography>
                                }
                                {
                                    etaValid &&
                                    <Box sx={{display: 'flex', flexDirection: 'column',}}>
                                        <Typography variant={'body2'}>
                                            {etaDate.format(DateUtils.dateFormat)}
                                        </Typography>
                                        <Typography variant={'caption'} component={'span'} marginInlineStart={0.5} color={'text.secondary'}>
                                            ({etaDate.fromNow()})
                                        </Typography>
                                    </Box>
                                }

                            </Tooltip>,
                        [ProductOnPurchaseListDataGridColumnNames.shipVia]: shippingCompanies.find(sc => sc.code === purchase.shipViaCode)?.title ?? purchase?.shipViaCode ?? "",
                        [ProductOnPurchaseListDataGridColumnNames.shipTo]: [purchase.shipmentCity, provinceName, countryName].filter(Boolean).join(", "),
                        [ProductOnPurchaseListDataGridColumnNames.note]:
                            <>
                                <div className={'w-100'}>
                                    <p {...dataGridCellTextProps} className={'word-line-break-anywhere d-inline'}>
                                        {purchase.note ? purchase.note?.substring(0, Math.min(40, noteLength)) + (noteLength > 40 ? '...' : '') : ''}
                                    </p>
                                    {
                                        noteLength > 40 &&
                                        <IconButton
                                            color={'paper.primary'}
                                            className={`d-inline m-1 p-0`}
                                            sx={{width: 30, height: 30,}}
                                            onClick={() => openViewNoteDialog(purchase)}
                                        >
                                            <VisibilityRounded/>
                                        </IconButton>
                                    }
                                </div>
                            </>,
                        [ProductOnPurchaseListDataGridColumnNames.action]: canManagePurchaseList
                            ? (
                                <IconButton
                                    size={'small'}
                                    onClick={() => openEditPartPurchaseEtaDialogState(purchase)}
                                    color={'primary'}
                                    sx={{backgroundColor: 'background.paper'}}
                                >
                                    <EditOutlined/>
                                </IconButton>
                            )
                            : undefined,
                    }
                }
            )
        });
    }, [purchases, shippingCompanies, countries, openEditPartPurchaseEtaDialogState, openViewNoteDialog, canManagePurchaseList]);

    return (
        <>
            <Box
                className={'d-flex flex-column'}
                sx={{
                    width: `min(90vw, ${width}px)`,
                    maxHeight: `min(90vh, ${height}px)`,
                    minHeight: `min(90vh, ${height}px)`,
                    overflowY: 'auto',
                    position: 'relative',
                    gap: 2,
                    padding: 1,
                }}
            >
                <Box sx={{
                    position: 'sticky',
                    top: -8,
                    zIndex: 10,
                    left: 0,
                    backgroundColor: 'background.paper',
                    padding: 1,
                    paddingInlineEnd: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    borderBottom: '1px solid',
                    borderBottomColor: 'divider',
                    cursor: 'grab',
                    '&:active': {
                        cursor: 'grabbing',
                    },
                }}>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                            <Tooltip title={"You can move this popover through the panel."} placement={'right'} followCursor>
                                <ControlCamera color={'primary'}/>
                            </Tooltip>
                            <Typography component={'div'} variant={'body2'} fontWeight={600} marginInlineStart={1}>
                                Purchase List for Part No: {partNo} in Warehouse: {warehouseCode}
                            </Typography>
                        </Box>
                        <IconButton onClick={close} color={'error'}>
                            <CloseOutlined/>
                        </IconButton>
                    </Box>
                </Box>
                <Box className={'draggable-popover-cancel'}>
                    <PartnerDataGrid
                        {...dataGridSaveConfig}
                        initialLayoutState={{
                            columns: columns,
                        }}
                        columns={dataGridColumns}
                        rows={dataGridRows}
                        visibleRows={5}
                        state={{
                            pagination: {currentPage: 1, pageSize: purchases.length, length: purchases.length},
                            loading: {state: loading},
                            density: dataGridDensity,
                        }}
                        hideFooter
                        hideExporting
                    />
                </Box>
            </Box>
            {
                canManagePurchaseList &&
                <EditPartPurchaseEtaDialog
                    open={editPartPurchaseEtaDialogState.open}
                    record={editPartPurchaseEtaDialogState.record}
                    onUpdate={updateRecord}
                    close={closeEditPartPurchaseEtaDialog}
                    shippingCompanies={shippingCompanies}
                    countries={countries}
                />
            }
            <ViewNoteDialog
                open={viewNoteDialogState.open}
                note={viewNoteDialogState.note}
                title={viewNoteDialogState.title}
                close={closeViewNoteDialog}
            />
        </>
    );
}


const ViewNoteDialog = ({open, note, title, close}) => {

    return (
        <>
            <Dialog open={open} setOpen={close} maxWidth={'sm'} sx={{zIndex: (theme) => theme.zIndex.modal + 2}} className={'draggable-popover-cancel'}>
                <DialogTitle id={'scroll-dialog-title'} className={'py-0'}>
                    <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                        {title ?? "Purchase Note"}
                    </p>
                </DialogTitle>
                <DialogContent dividers>
                    <DialogContentText sx={{marginBlockEnd: 2,}}>
                        {note ?? ""}
                    </DialogContentText>
                </DialogContent>
                <DialogActions className={'d-flex align-items-center justify-content-between p-0'}>
                    <Button
                        disabled={!open}
                        variant={'dialog'}
                        color={'primary'}
                        type={'button'}
                        onClick={() => close()}
                        borderRadius={'0 8px 0 8px'}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}


export default ProductOnPurchasePopover;